import React, { useState, useEffect } from 'react';
import { Modal, Input, Form, Radio, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getAlert, insertData, getDataWithComp } from '../../../utils/SamuiUtils';

export default function AddCategoryUnitModal({
  open,
  onClose,
  onSuccess,
  existingGroups = [],
  existingUnits = [],
  onUnitsRefresh,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('category'); // 'category' | 'unit'
  const [units, setUnits] = useState(existingUnits);

  useEffect(() => {
    setUnits(existingUnits);
  }, [existingUnits]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setMode('category');
      // ตั้งค่า default values
      form.setFieldsValue({
        Group_Code: 'G', // Default: สินค้า (ทั่วไป)
        Unit_Type: 1,    // Default: ทั่วไป
      });
    }
  }, [open, form]);

  const validateGroupCodeName = (_, value) => {
    if (!value) return Promise.reject(new Error('กรุณากรอกรหัสหมวดหมู่'));

    const isDuplicate = existingGroups.some(
      (g) =>
        String(g.code || '').toLowerCase() ===
        String(value || '').toLowerCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error('รหัสหมวดหมู่นี้มีอยู่ในระบบแล้ว'));
    }

    return Promise.resolve();
  };

  const validateGroupName = (_, value) => {
    if (!value) return Promise.reject(new Error('กรุณากรอกชื่อหมวดหมู่'));

    const isDuplicate = existingGroups.some(
      (g) =>
        String(g.name || '').toLowerCase() ===
        String(value || '').toLowerCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error('ชื่อหมวดหมู่นี้มีอยู่ในระบบแล้ว'));
    }

    return Promise.resolve();
  };

  const validateUnitName = (_, value) => {
    if (!value) return Promise.reject(new Error('กรุณากรอกชื่อหน่วย'));

    const isDuplicate = existingUnits.some(
      (u) =>
        String(u.name || '').toLowerCase() ===
        String(value || '').toLowerCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error('ชื่อหน่วยนี้มีอยู่ในระบบแล้ว'));
    }

    return Promise.resolve();
  };

  const handleSubmitCategory = async () => {
    try {
      await form.validateFields(['Group_CodeName', 'Group_Name', 'Unit_Id', 'Group_Code']);
      const values = form.getFieldsValue();
      setLoading(true);

      const compId = localStorage.getItem('company');

      // ตรวจสอบค่าก่อนส่ง
      if (!values.Unit_Id) {
        getAlert('FAILED', 'กรุณาเลือกหน่วย');
        setLoading(false);
        return;
      }

      if (!compId) {
        getAlert('FAILED', 'ไม่พบ Company ID');
        setLoading(false);
        return;
      }

      if (!values.Group_Code) {
        getAlert('FAILED', 'กรุณาเลือกประเภทหมวดหมู่');
        setLoading(false);
        return;
      }

      // ✅ ตรวจสอบซ้ำกับ DB
      let codeCheck, nameCheck;
      try {
        codeCheck = await getDataWithComp(
          'Tb_Set_ItmGroup',
          `AND Group_CodeName='${values.Group_CodeName.trim()}' AND Group_Status='Y'`
        );
      } catch (e) {
        console.error('❌ Error checking Group_CodeName:', e);
        getAlert('FAILED', 'ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง');
        setLoading(false);
        return;
      }

      if (codeCheck && codeCheck.length > 0) {
        getAlert('FAILED', `รหัสหมวดหมู่ "${values.Group_CodeName}" มีอยู่ในระบบแล้ว`);
        setLoading(false);
        return;
      }

      try {
        nameCheck = await getDataWithComp(
          'Tb_Set_ItmGroup',
          `AND Group_Name='${values.Group_Name.trim()}' AND Group_Status='Y'`
        );
      } catch (e) {
        console.error('❌ Error checking Group_Name:', e);
        getAlert('FAILED', 'ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง');
        setLoading(false);
        return;
      }

      const fields = 'Group_CodeName,Group_Name,Group_Status,Comp_Id,Unit_Id,Doc_Type,Group_Code';
      const valuesArray = [[
        values.Group_CodeName.trim(),
        values.Group_Name.trim(),
        'Y',
        Number(compId),
        Number(values.Unit_Id),
        1, // Doc_Type: 1 = สินค้า
        values.Group_Code, // G = ทั่วไป, S = บริการ
      ]];

      console.debug('🔍 Calling insertData with:', {
        table: 'Tb_Set_ItmGroup',
        fields,
        values: valuesArray,
        compId: Number(compId),
        unitId: Number(values.Unit_Id)
      });

      const response = await insertData('Tb_Set_ItmGroup', fields, valuesArray);

      console.debug('📦 Response from insertData:', response);
      console.debug('📊 Response type:', typeof response);
      console.debug('✓ Has ok property:', 'ok' in (response || {}));
      console.debug('✓ response.ok value:', response?.ok);

      if (response && response.ok !== false) {
        // insertData คืนค่า {ok: true, data: [insertId]} หรือ null ถ้า error
        const newGroupId = response.data?.[0] || response.insertId;
        console.debug('✅ Insert successful! newGroupId:', newGroupId);
        
        // ✅ รอให้ onSuccess ทำงานเสร็จก่อนปิด modal
        await onSuccess?.('category', newGroupId);
        
        getAlert('OK', 'เพิ่มหมวดหมู่สำเร็จ');
        onClose();
      } else {
        console.error('❌ Insert group failed:', response);
        const errorMsg = response?.error || response?.message || 'ไม่สามารถเพิ่มหมวดหมู่ได้';
        getAlert('FAILED', `เกิดข้อผิดพลาด: ${errorMsg}`);
      }
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error('❌ Error adding category:', error);
      const errorMsg = error?.response?.data?.error || error?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์';
      getAlert('FAILED', `เกิดข้อผิดพลาด: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUnit = async () => {
    try {
      await form.validateFields(['Unit_Name']);
      const values = form.getFieldsValue();
      setLoading(true);

      const compId = localStorage.getItem('company');

      // ✅ ตรวจสอบซ้ำกับ DB
      let nameCheck;
      try {
        nameCheck = await getDataWithComp(
          'Tb_Set_Unit',
          `AND Unit_Name='${values.Unit_Name.trim()}' AND Unit_Status='Y'`
        );
      } catch (e) {
        console.error('❌ Error checking Unit_Name:', e);
        getAlert('FAILED', 'ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง');
        setLoading(false);
        return;
      }

      const fields = 'Unit_Name,Unit_Status,Comp_Id,Unit_Type';
      const valuesArray = [[
        values.Unit_Name.trim(),
        'Y',
        Number(compId),
        Number(values.Unit_Type || 1),
      ]];

      const response = await insertData('Tb_Set_Unit', fields, valuesArray);

      if (response && response.ok !== false) {
        const newUnitId = response.data?.[0] || response.insertId;
        
        // รีเฟรชรายการหน่วย
        await onUnitsRefresh?.();
        
        getAlert('OK', 'เพิ่มหน่วยสำเร็จ', () => {
          // สลับกลับไปโหมดหมวดหมู่และเลือกหน่วยที่เพิ่งสร้าง
          setMode('category');
          form.setFieldsValue({ Unit_Id: newUnitId });
        });
      } else {
        console.error('❌ Insert unit failed:', response);
        const errorMsg = response?.error || response?.message || 'ไม่สามารถเพิ่มหน่วยได้';
        getAlert('FAILED', `เกิดข้อผิดพลาด: ${errorMsg}`);
      }
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error('❌ Error adding unit:', error);
      const errorMsg = error?.response?.data?.error || error?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์';
      getAlert('FAILED', `เกิดข้อผิดพลาด: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOk = () => {
    if (mode === 'category') {
      handleSubmitCategory();
    } else {
      handleSubmitUnit();
    }
  };

  return (
    <Modal
      open={open}
      title={mode === 'category' ? 'เพิ่มหมวดหมู่ใหม่' : 'เพิ่มหน่วยใหม่'}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={loading}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={500}
      destroyOnClose
    >
      <div style={{ marginTop: 16 }}>
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="category">เพิ่มหมวดหมู่</Radio.Button>
          <Radio.Button value="unit">เพิ่มหน่วย</Radio.Button>
        </Radio.Group>

        <Form form={form} layout="vertical">
          {mode === 'category' ? (
            <>
              <Form.Item
                name="Group_CodeName"
                label="รหัสหมวดหมู่"
                rules={[{ validator: validateGroupCodeName }]}
                extra={
                  <span style={{ fontSize: '12px', color: '#f97316' }}>
                    ⚠️ ใช้สำหรับสร้างรหัสสินค้า
                  </span>
                }
              >
                <Input placeholder="เช่น SH, TS, PN" maxLength={10} size="large" style={{height:"35px" }}/>
              </Form.Item>

              <Form.Item
                name="Group_Name"
                label="ชื่อหมวดหมู่"
                rules={[{ validator: validateGroupName }]}
              >
                <Input placeholder="เช่น เสื้อเชิ้ต, เสื้อยืด, กางเกง" maxLength={50} size="large" style={{height:"35px" }} />
              </Form.Item>

              <Form.Item
                name="Group_Code"
                label="ประเภทหมวดหมู่"
                rules={[
                  { required: true, message: 'กรุณาเลือกประเภทหมวดหมู่' },
                ]}
              >
                <Select
                  placeholder="เลือกประเภท"
                  size="large"
                  options={[
                    { value: 'G', label: 'ทั่วไป (สินค้า)' },
                    { value: 'S', label: 'บริการ' },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="Unit_Id"
                label="หน่วย"
                rules={[
                  { required: true, message: 'กรุณาเลือกหน่วย' },
                ]}
              >
                <Select
                  placeholder="เลือกหน่วย"
                  size="large"
                  options={units.map((u) => ({
                    value: u.id,
                    label: u.name,
                  }))}
                />
              </Form.Item>

              <div style={{ marginTop: -16, marginBottom: 16 }}>
                <button
                  type="button"
                  onClick={() => setMode('unit')}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#218838';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#28a745';
                  }}
                >
                  <PlusOutlined />
                  <span>เพิ่มหน่วยใหม่</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <Form.Item
                name="Unit_Name"
                label="ชื่อหน่วย"
                rules={[{ validator: validateUnitName }]}
              >
                <Input placeholder="เช่น ชิ้น, กล่อง, โหล" maxLength={30} size="large" style={{height:"35px" }} />
              </Form.Item>

              {/* Unit_Type ถูกซ่อนเพราะ default เป็น ทั่วไป (1) อยู่แล้ว */}
              <Form.Item name="Unit_Type" hidden>
                <Input type="hidden" />
              </Form.Item>
            </>
          )}
        </Form>
      </div>
    </Modal>
  );
}
